import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { IMaintenanceJob } from "../../maintenance_jobs/MaintenanceJobsModels";
import { queryMaintenanceJobs } from "../../maintenance_jobs/MaintenanceJobsAPI";
import { RootState } from "../Store";
import { beginRequest, completeRequest, rejectRequest, RequestState } from "./RequestState";
import { LoadingState } from "../LoadingState";

interface IMaintenanceJobsState {
  [key: string]: {
    rawJobs: IMaintenanceJob[];
    error?: string;
    loading: boolean;
    request?: RequestState;
  };
}

const initialState: IMaintenanceJobsState = {};
const EMPTY_JOBS: IMaintenanceJob[] = [];

export const multiSitesMaintenanceJobsSlice = createSlice({
  name: "multiSitesMaintenanceJobs",
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchMaintenanceJobs.fulfilled, (state, action) => {
        const { siteId, jobs } = action.payload;
        const current = state[siteId];
        if (!current?.request || !completeRequest(current.request, action.meta.requestId)) {
          return;
        }
        current.rawJobs = jobs;
        current.loading = false;
        current.error = undefined;
      })
      .addCase(fetchMaintenanceJobs.rejected, (state, action) => {
        const { siteId } = action.meta.arg;
        const currentSiteState = state[siteId];
        if (!currentSiteState?.request || !rejectRequest(currentSiteState.request, action)) {
          return;
        }
        currentSiteState.loading = false;
        if (action.meta.aborted) {
          return;
        }

        const status = (action.payload as FetchMaintenanceJobsError | undefined)?.status;
        const userFacingError =
          status === 401 || status === 403
            ? "You do not have permission to load maintenance jobs"
            : status === 400
              ? "Failed to load maintenance jobs due to invalid request parameters"
              : "Failed to load maintenance jobs";

        console.error("Failed to load maintenance jobs", { status, error: action.error });
        currentSiteState.error = userFacingError;
      })
      .addCase(fetchMaintenanceJobs.pending, (state, action) => {
        const { siteId } = action.meta.arg;
        const current = (state[siteId] ??= { rawJobs: [], loading: false });
        current.request ??= { loadingState: LoadingState.None };
        beginRequest(current.request, action.meta.requestId);
        current.loading = true;
      }),
});

type FetchMaintenanceJobsArgs = {
  siteId: string;
};

type FetchMaintenanceJobsError = {
  status?: number;
};

export const fetchMaintenanceJobs = createAsyncThunk(
  "multiSitesMaintenanceJobs/fetchMaintenanceJobs",
  async ({ siteId }: FetchMaintenanceJobsArgs, { getState, rejectWithValue }) => {
    try {
      const jobs = await queryMaintenanceJobs(siteId, (getState() as RootState).site.trackers);
      return { siteId, jobs: jobs };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue({ status: error.response?.status } as FetchMaintenanceJobsError);
      }

      throw error;
    }
  }
);

export const selectCurrentSiteMaintenanceJobs = (state: RootState) =>
  state.multiSitesMaintenanceJobs[state.site.siteId]?.rawJobs ?? EMPTY_JOBS;

export const multiSitesMaintenanceJobsReducer = multiSitesMaintenanceJobsSlice.reducer;
